import "./MoreInformation.css";

import { useState } from "react";

import { ISoccerMatch } from "../../../../../../types";
import { usePreMatchStats, ITeamForm, Result } from "../../../../hooks/usePreMatchStats";

const RESULT_BG: Record<Result, string> = {
  W: "#008001",
  L: "#cf1a17",
  D: "#9ba4ac",
};

function ResultBadge({ result }: { result: Result }) {
  return (
    <span className="mi-form-badge" style={{ background: RESULT_BG[result] }}>
      {result}
    </span>
  );
}

function TeamFormColumn({ team }: { team: ITeamForm }) {
  return (
    <div className="mi-form-col">
      <span className="mi-form-team-name">{team.name}</span>
      <div className="mi-form-badges">
        {team.results.map((r, i) => (
          <ResultBadge key={i} result={r} />
        ))}
      </div>
      <div className="mi-form-goals">
        <img className="mi-form-goals-icon" src="/images/box-score/football.svg" alt="" />
        <span className="mi-form-goals-label">Goals/Match</span>
      </div>
      <span className="mi-form-goals-value">{team.goalsPerMatch}</span>
    </div>
  );
}

export function MoreInformation({ soccerMatch }: { soccerMatch: ISoccerMatch }) {
  const [expanded, setExpanded] = useState(false);
  const { homeTeam, awayTeam, h2hMatches } = usePreMatchStats(soccerMatch);
  const stageName = soccerMatch.stage?.name ?? "";
  const hasFormData = homeTeam.results.length > 0 || awayTeam.results.length > 0;
  const hasH2H = h2hMatches.length > 0;

  return (
    <div className="mi-root">
      {expanded && (
        <div className="mi-content">
          {/* Stage badge */}
          {stageName && (
            <div className="mi-stage">
              <img className="mi-stage-icon" src="/images/box-score/leaderboard.svg" alt="" />
              <span className="mi-stage-text">{stageName}</span>
            </div>
          )}

          {/* Team Form */}
          {hasFormData && (
            <div className="mi-section">
              <p className="mi-section-title">
                Team form{" "}
                <span className="mi-section-subtitle">
                  (last {Math.max(homeTeam.results.length, awayTeam.results.length)} matches)
                </span>
              </p>
              <div className="mi-form-card">
                <TeamFormColumn team={homeTeam} />
                <div className="mi-form-divider" />
                <TeamFormColumn team={awayTeam} />
              </div>
            </div>
          )}

          {/* Head-to-Head */}
          {hasH2H && (
            <div className="mi-section">
              <p className="mi-section-title">
                Head-to-Head{" "}
                <span className="mi-section-subtitle">
                  (last {h2hMatches.length} {h2hMatches.length === 1 ? "match" : "matches"})
                </span>
              </p>
              <div className="mi-h2h-card">
                {h2hMatches.map((match, i) => (
                  <div key={i} className="mi-h2h-row">
                    <div className="mi-h2h-left">
                      {!match.isDraw && (
                        <img
                          className="mi-h2h-flag"
                          src={match.winnerFlag}
                          alt={match.winnerName}
                        />
                      )}
                      <span className="mi-h2h-result">
                        {match.isDraw ? (
                          <>
                            Draw{" "}
                            <strong>
                              {match.homeScore} - {match.awayScore}
                            </strong>
                          </>
                        ) : (
                          <>
                            {match.winnerName} won{" "}
                            <strong>
                              {match.homeScore} - {match.awayScore}
                            </strong>
                          </>
                        )}
                      </span>
                    </div>
                    <span className="mi-h2h-date">{match.date}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Toggle */}
      <button
        className="mi-toggle"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <span className="mi-toggle-text">
          {expanded ? "Less information" : "More information"}
        </span>
        <img
          className={`mi-toggle-icon ${expanded ? "mi-toggle-icon--up" : ""}`}
          src="/images/arrow-right-white.svg"
          alt=""
        />
      </button>

    </div>
  );
}
